import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import VisitCard from "../../components/visits/VisitCard";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { Link } from "react-router-dom";
import { Sparkles, Users, Inbox, X, Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { todayIST, istDayKey, currentMonthIST, monthRangeIST } from "../../lib/datetime";
import { teamCountLabel } from "../../lib/population";
import StickyHeading from "../../components/common/StickyHeading";
import ScopeToggle from "../../components/ScopeToggle";
import DeviationCounts from "../../components/visits/DeviationCounts";

const ALL = "__all__";

export default function TeamVisitsPage() {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [allMonth, setAllMonth] = useState([]); // for per-SO summary
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [soId, setSoId] = useState(ALL);
  // Mine / Team. A manager's own calls are in the team list already (a team
  // INCLUDES its manager, B6) but had no name of their own — "Mine" is the
  // same `so_id=self` narrowing the dashboard's own-work panel applies.
  const [scope, setScope] = useState("team");
  const [category, setCategory] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Fetch the AM's team (SOs reporting to them).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        // A TEAM INCLUDES ITS MANAGER (B6) — the manager is a member, not an
        // addition, and they log their own calls, so they are filterable here.
        // An IC manager with zero reportees still gets a usable one-entry
        // filter.
        const sos = data.filter(
          (u) =>
            u.is_active &&
            (u.id === user?.id ||
              (u.role === "sales_officer" && u.manager_id === user?.id))
        );
        setTeam(sos);
      } catch (_) {
        /* non-fatal */
      }
    })();
  }, [user]);

  // Fetch month-to-date visits (for per-SO summary).
  useEffect(() => {
    let cancelled = false;
    // The month boundary is the company's, not the viewer's: on the 1st before
    // 05:30 IST a local-midnight `new Date()` is still last month.
    const { year, month } = currentMonthIST();
    const startIso = monthRangeIST(year, month).first;
    api
      .get("/doctor-visits", { params: { date_from: startIso } })
      .then(({ data }) => !cancelled && setAllMonth(data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the filtered list.
  useEffect(() => {
    const params = {};
    const effectiveSo = scope === "me" ? user?.id : soId;
    if (effectiveSo && effectiveSo !== ALL) params.so_id = effectiveSo;
    if (category !== ALL) params.category = category;
    if (from) params.date_from = from;
    if (to) params.date_to = to;

    let cancelled = false;
    setLoading(true);
    api
      .get("/doctor-visits", { params })
      .then(({ data }) => !cancelled && setVisits(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [scope, soId, category, from, to, user?.id]);

  const summary = useMemo(() => {
    const today = todayIST();
    const map = {};
    for (const m of team) {
      map[m.id] = { id: m.id, name: m.full_name, today: 0, month: 0 };
    }
    for (const v of allMonth) {
      const row = map[v.sales_officer_id];
      if (!row) continue;
      row.month += 1;
      // RC-8: the IST calendar day of the value, not the raw string — the API
      // returns a date-only day here, but a full instant must bucket the same.
      if (istDayKey(v.visit_date) === today) row.today += 1;
    }
    return Object.values(map).sort((a, b) => b.month - a.month);
  }, [team, allMonth]);

  const hasFilters = soId !== ALL || category !== ALL || !!from || !!to;
  const clear = () => {
    setSoId(ALL);
    setCategory(ALL);
    setFrom("");
    setTo("");
  };

  // B6 (RC-9), which REVERSED the rule the old comment here encoded: a team
  // INCLUDES its manager. The count is therefore inclusive — and because that
  // is surprising to anyone who reads "team" as "my reports", an inclusive
  // count must ship its split ("4 reports + you") rather than assert a total.
  // `teamCountLabel` also refuses to call a mixed population "sales officers":
  // `team` here holds the manager and their reportees, not one role.
  const selfInTeam = team.some((u) => u.id === user?.id);
  const reportCount = team.filter((u) => u.id !== user?.id).length;
  const teamLabel = teamCountLabel({
    total: team.length,
    reports: reportCount,
    includesManager: selfInTeam,
    noun: "in your team",
  });

  return (
    <div className="space-y-6" data-testid="team-visits-page">
      <StickyHeading
        icon={Sparkles}
        label="Team Daily Call Report"
        title="Team visits"
        subtitle={
          reportCount === 0
            ? "Your own calls. Assign doctors to yourself to log visits."
            : `Calls across your team — ${teamLabel}.`
        }
        testId="team-visits-heading"
        actions={
          /* Both manager tiers log their own calls for doctors assigned to
             them (§1 Fix A) — `routes_visits.VISIT_FILER_ROLES`. */
          ["assistant_manager", "admin_manager"].includes(user?.role) && (
            <Link
              to="/doctor-visits/new"
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-callisto-navy text-white h-11 px-5 text-sm font-semibold hover:bg-callisto-navy/90 shrink-0"
              data-testid="team-log-visit-cta"
            >
              <Plus className="h-4 w-4" />
              Log a visit
            </Link>
          )
        }
      />

      <ScopeToggle scope={scope} onChange={setScope} testIdPrefix="team-visits" />

      {/* Per-SO summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {summary.length === 0 ? (
          <div className="col-span-full text-sm text-callisto-muted">No team members yet.</div>
        ) : (
          summary.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSoId((cur) => (cur === s.id ? ALL : s.id))}
              className={`text-left bg-white border rounded-xl p-3 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                soId === s.id ? "border-callisto-gold" : "border-callisto"
              }`}
              data-testid={`team-so-summary-${s.id}`}
            >
              <p className="text-sm font-semibold text-callisto-navy truncate">{s.name}</p>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-callisto-muted">
                Today <span className="font-bold text-callisto-navy ml-1">{s.today}</span>
                <span className="mx-1.5">·</span>
                Month <span className="font-bold text-callisto-navy ml-1">{s.month}</span>
              </p>
            </button>
          ))
        )}
      </div>

      {/* Plan deviation (§2 Session 4): four numbers per officer for the
          filtered range — month to date until a date is picked. */}
      <div data-testid="team-plan-deviation">
        <DeviationCounts
          from={from}
          to={to}
          soId={scope === "me" ? user?.id : soId !== ALL ? soId : undefined}
          testIdPrefix="team-deviation"
        />
      </div>

      {/* Filters */}
      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Select value={soId} onValueChange={setSoId}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="team-filter-so">
              <SelectValue placeholder="Officer / manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All officers &amp; managers</SelectItem>
              {team.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="team-filter-category">
              <SelectValue placeholder="Doctor category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All categories</SelectItem>
              <SelectItem value="A">A — High value</SelectItem>
              <SelectItem value="B">B — Medium value</SelectItem>
              <SelectItem value="C">C — Standard</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={to || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="team-filter-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="team-filter-to"
          />
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            onClick={clear}
            className="mt-2 h-9 text-callisto-muted hover:text-callisto-navy"
            data-testid="team-filters-clear"
          >
            <X className="h-3 w-3 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-36 w-full rounded-xl" />
          ))}
        </div>
      ) : visits.length === 0 ? (
        <EmptyState />
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
          data-testid="team-visits-grid"
        >
          {visits.map((v) => (
            <VisitCard key={v.id} visit={v} showOfficer />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
      <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
        <Inbox className="h-6 w-6 text-callisto-navy" />
      </div>
      <p className="text-sm font-semibold text-callisto-navy">No visits found</p>
      <p className="mt-1 text-xs text-callisto-muted max-w-sm">
        Try widening the date range or clearing filters.
      </p>
    </div>
  );
}
